import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, CheckCircle2, Lock, X } from 'lucide-react';
interface BundleUpsellPopupProps {
  isOpen: boolean;
  onClose: () => void;
  strikethroughPrice?: string;
  specialPrice?: string;
}
export function BundleUpsellPopup({
  isOpen,
  onClose,
  strikethroughPrice = '$24.97 USD',
  specialPrice = '$21.39 USD'
}: BundleUpsellPopupProps) {
  const FULL_BUNDLE_URL = 'https://nutrinatural.online/cart/54018735997257:1';
  const MAIN_PRODUCT_URL = 'https://nutrinatural.online/cart/54017585447241:1';
  const fireInitiateCheckout = () => {
    try {
      if (typeof (window as any).fbq === 'function') {
        ;
        (window as any).fbq('track', 'InitiateCheckout');
      }
    } catch (e) {
      // silently fail
    }
  };
  // Lock body scroll when popup is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  return <AnimatePresence data-id="element-129">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-3 md:p-6 lg:p-8" onClick={onClose} data-id="element-130">
          <motion.div initial={{
        scale: 0.9,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.9,
        opacity: 0,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="bg-white rounded-2xl md:rounded-[2rem] shadow-2xl w-full max-w-[420px] md:max-w-lg lg:max-w-xl overflow-hidden relative border-[3px] md:border-4 border-[#FF8A65] max-h-[95vh] md:max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-131">
            {/* Close button */}
            <button onClick={onClose} className="absolute top-3 right-3 md:top-4 md:right-4 text-white/80 hover:text-white z-20 transition-colors" aria-label="Cerrar" data-id="element-132">
              <X className="w-5 h-5 md:w-6 md:h-6" data-id="element-133" />
            </button>

            {/* Scrollable content */}
            <div className="overflow-y-auto flex-1 overscroll-contain" data-id="element-134">
              {/* Header */}
              <div className="bg-[#FF8A65] text-white text-center pt-5 pb-4 md:pt-8 md:pb-6 lg:pt-10 lg:pb-8 px-4 md:px-8 relative" data-id="element-135">
                <div className="flex justify-center mb-2 md:mb-3" data-id="element-136">
                  <div className="bg-white/20 p-2 md:p-3 rounded-full" data-id="element-137">
                    <AlertTriangle className="w-6 h-6 md:w-8 md:h-8 text-white" data-id="element-138" />
                  </div>
                </div>
                <h2 className="text-xl md:text-2xl lg:text-3xl font-black leading-tight mb-1 md:mb-2" data-id="element-139">
                  ¡ESPERA! NO TE VAYAS INCOMPLETA
                </h2>
                <p className="text-white/90 font-medium text-xs md:text-sm lg:text-base" data-id="element-140">
                  Llévate el ecosistema completo con un descuento único.
                </p>
              </div>

              <div className="p-4 md:p-6 lg:p-8" data-id="element-141">
                {/* Flashing Badge */}
                <div className="flex justify-center mb-4 md:mb-6 -mt-7 md:-mt-9 lg:-mt-10 relative z-10" data-id="element-142">
                  <span className="inline-block bg-yellow-400 text-[#E65100] font-black text-sm md:text-base lg:text-lg px-4 md:px-6 py-1.5 md:py-2 rounded-full shadow-lg animate-pulse border-2 border-white" data-id="element-143">
                    🔥 50% OFF EN LOS EXTRAS 🔥
                  </span>
                </div>

                {/* Copy */}
                <p className="text-gray-700 text-center text-sm md:text-base lg:text-lg mb-4 md:mb-6 leading-relaxed" data-id="element-144">
                  Nutrir con calma es más fácil cuando tienes todas las
                  herramientas. Llévate los recetarios de alfajores y el sistema
                  de cumpleaños por una fracción de su valor.
                </p>

                {/* What's Included */}
                <div className="bg-[#FDFBF7] rounded-xl md:rounded-2xl p-3 md:p-5 lg:p-6 border border-gray-200 mb-4 md:mb-6" data-id="element-145">
                  <p className="text-[10px] md:text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 md:mb-3 text-center" data-id="element-146">
                    TU PAQUETE COMPLETO INCLUYE:
                  </p>
                  <ul className="space-y-2 md:space-y-3" data-id="element-147">
                    <li className="flex items-start gap-2 md:gap-3" data-id="element-148">
                      <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-[#4CAF50] shrink-0 mt-0.5" data-id="element-149" />
                      <span className="text-gray-800 font-medium text-xs md:text-sm lg:text-base" data-id="element-150">
                        NutriKids Completo + Regalo Especial
                      </span>
                    </li>
                    <li className="flex items-start gap-2 md:gap-3" data-id="element-151">
                      <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-[#4CAF50] shrink-0 mt-0.5" data-id="element-152" />
                      <span className="text-gray-800 font-medium text-xs md:text-sm lg:text-base" data-id="element-153">
                        15 Recetas de Alfajores, Cookies y Bombones
                      </span>
                    </li>
                    <li className="flex items-start gap-2 md:gap-3" data-id="element-154">
                      <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-[#4CAF50] shrink-0 mt-0.5" data-id="element-155" />
                      <span className="text-gray-800 font-medium text-xs md:text-sm lg:text-base" data-id="element-156">
                        Cumpleaños Saludables: Sistema para Fiestas
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Pricing */}
                <div className="text-center mb-4 md:mb-6" data-id="element-157">
                  <div className="flex items-center justify-center gap-2 md:gap-3 mb-1" data-id="element-158">
                    <span className="text-gray-400 line-through text-base md:text-lg font-medium" data-id="element-159">
                      {strikethroughPrice}
                    </span>
                    <span className="text-2xl md:text-3xl lg:text-4xl font-black text-[#2E7D32]" data-id="element-160">
                      {specialPrice}
                    </span>
                  </div>
                  <p className="text-[10px] md:text-xs text-[#E65100] font-bold uppercase tracking-wider flex items-center justify-center gap-1" data-id="element-161">
                    <Lock className="w-3 h-3" data-id="element-162" /> Precio especial válido solo en
                    esta pantalla
                  </p>
                </div>

                {/* CTAs */}
                <div className="space-y-3" data-id="element-163">
                  <a href={FULL_BUNDLE_URL} onClick={fireInitiateCheckout} className="w-full block bg-[#4CAF50] hover:bg-[#388E3C] text-white text-center text-sm md:text-base lg:text-lg font-black rounded-xl md:rounded-2xl shadow-[0_5px_0_#2E7D32] hover:shadow-[0_2px_0_#2E7D32] hover:translate-y-1 transition-all px-4 py-3.5 md:py-4 lg:py-5 animate-[pulse_2s_infinite]" data-id="element-164">
                    SÍ! QUIERO EL PAQUETE COMPLETO POR{' '}
                    {specialPrice.replace(' USD', '')}
                  </a>

                  <a href={MAIN_PRODUCT_URL} onClick={fireInitiateCheckout} className="w-full block text-center bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800 text-xs md:text-sm font-semibold rounded-xl md:rounded-2xl px-4 py-2.5 md:py-3 transition-all" data-id="element-165">
                    No gracias, continuar solo con NutriKids →
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}